import React, { createContext, useContext, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useFollowingIds } from '@/data/queries/useFollowingIds';
import { useFollowUser, useUnfollowUser } from '@/data/mutations/useFollowMutations';
import { keys } from '@/data/keys';
import { logError } from '@/lib/logError';

interface FollowContextType {
  followingUsers: Set<string>;
  followingLoaded: boolean;
  followUser: (userId: string) => Promise<{ success: boolean; error?: string }>;
  unfollowUser: (userId: string) => Promise<{ success: boolean; error?: string }>;
  isFollowing: (userId: string) => boolean;
  refreshFollowing: () => Promise<void>;
}

const FollowContext = createContext<FollowContextType | undefined>(undefined);

/**
 * Provider-independent follow state. Backed by the shared React Query cache,
 * so it works identically whether or not a FollowProvider is mounted above.
 */
function useFollowState(): FollowContextType {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  const { data: followingUsers, isSuccess } = useFollowingIds(userId);
  const followMutation = useFollowUser(userId);
  const unfollowMutation = useUnfollowUser(userId);

  const followingSet = useMemo(() => followingUsers ?? new Set<string>(), [followingUsers]);

  const refreshFollowing = useCallback(async () => {
    if (!userId) return;
    await qc.invalidateQueries({ queryKey: keys.follows.followingIds(userId) });
  }, [userId, qc]);

  const followUser = useCallback(
    async (targetUserId: string) => {
      if (!userId) return { success: false, error: 'Not authenticated' };
      try {
        await followMutation.mutateAsync(targetUserId);
        return { success: true };
      } catch (error) {
        logError(error, { where: 'FollowContext.followUser', targetUserId });
        return { success: false, error: (error as Error).message };
      }
    },
    [userId, followMutation],
  );

  const unfollowUser = useCallback(
    async (targetUserId: string) => {
      if (!userId) return { success: false, error: 'Not authenticated' };
      try {
        await unfollowMutation.mutateAsync(targetUserId);
        return { success: true };
      } catch (error) {
        logError(error, { where: 'FollowContext.unfollowUser', targetUserId });
        return { success: false, error: (error as Error).message };
      }
    },
    [userId, unfollowMutation],
  );

  const isFollowing = useCallback((id: string) => followingSet.has(id), [followingSet]);

  return {
    followingUsers: followingSet,
    followingLoaded: Boolean(userId) && isSuccess,
    followUser,
    unfollowUser,
    isFollowing,
    refreshFollowing,
  };
}

export const useFollow = (): FollowContextType => {
  const local = useFollowState();
  const context = useContext(FollowContext);
  return context ?? local;
};

/**
 * Thin context wrapper around the shared React Query cache.
 * Owns the realtime subscription; the state itself lives in `useFollowState`.
 */
export const FollowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useAuth();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  const value = useFollowState();

  // Real-time listener — invalidate the cache when follows change for this user.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel('follows-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'follows',
          filter: `follower_id=eq.${userId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: keys.follows.followingIds(userId) });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, qc]);


  return <FollowContext.Provider value={value}>{children}</FollowContext.Provider>;
};
